// WebSocket service for real-time communication with Django backend

// Use environment variable for WebSocket URL, fallback to localhost
const WS_BASE_URL = import.meta.env.VITE_WS_BASE_URL || 'ws://localhost:8000/ws'

class WebSocketService {
  constructor() {
    this.socket = null
    this.isConnected = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 1000 // Start with 1 second
    this.eventListeners = new Map()
    this.messageQueue = []
  }

  connect(url = `${WS_BASE_URL}/counter/`) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected')
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      try {
        console.log(`Connecting to WebSocket: ${url}`)
        this.socket = new WebSocket(url)

        this.socket.onopen = (event) => {
          console.log('WebSocket connected successfully')
          this.isConnected = true
          this.reconnectAttempts = 0
          this.reconnectDelay = 1000
          
          // Send any queued messages
          this.flushMessageQueue()
          
          // Notify listeners
          this.notifyListeners('open', { event })
          resolve()
        }

        this.socket.onclose = (event) => {
          console.log('WebSocket connection closed:', event.code, event.reason)
          this.isConnected = false
          
          // Notify listeners
          this.notifyListeners('close', { event, code: event.code, reason: event.reason })
          
          // Attempt to reconnect if not intentionally closed
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect()
          }
        }

        this.socket.onerror = (error) => {
          console.error('WebSocket error:', error)
          console.error('WebSocket readyState:', this.socket.readyState)
          console.error('WebSocket url:', url)
          
          this.notifyListeners('error', { error, readyState: this.socket.readyState, url })
          
          if (this.socket.readyState === WebSocket.CONNECTING) {
            reject(new Error(`Failed to connect to WebSocket at ${url}. Check if Django server is running with WebSocket support.`))
          }
        }

        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            console.log('WebSocket message received:', data)
            
            // Notify listeners based on message type
            this.notifyListeners('message', data)
            this.notifyListeners(data.type, data)
            
          } catch (error) {
            console.error('Error parsing WebSocket message:', error)
            this.notifyListeners('error', { error })
          }
        }

      } catch (error) {
        console.error('Error creating WebSocket connection:', error)
        reject(error)
      }
    })
  }

  disconnect() {
    if (this.socket) {
      console.log('Disconnecting WebSocket')
      this.socket.close(1000, 'Client disconnecting')
      this.socket = null
      this.isConnected = false
    }
  }

  send(data) {
    if (this.isConnected && this.socket) {
      const message = JSON.stringify(data)
      console.log('Sending WebSocket message:', message)
      this.socket.send(message)
    } else {
      console.log('WebSocket not connected, queuing message:', data)
      this.messageQueue.push(data)
    }
  }

  // Event listener management
  addEventListener(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set())
    }
    this.eventListeners.get(event).add(callback)
  }

  removeEventListener(event, callback) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).delete(callback)
    }
  }

  notifyListeners(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in WebSocket event listener for ${event}:`, error)
        }
      })
    }
  }

  // Helper methods for common operations
  ping() {
    this.send({ type: 'ping' })
  }

  getCurrentCounter() {
    this.send({ type: 'get_current' })
  }

  scheduleReconnect() {
    this.reconnectAttempts++
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000)
    
    console.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`)
    
    setTimeout(() => {
      if (!this.isConnected) {
        this.connect().catch(error => {
          console.error('Reconnect failed:', error)
        })
      }
    }, delay)
  }

  flushMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift()
      this.send(message)
    }
  }

  // Utility methods
  getConnectionState() {
    if (!this.socket) return 'CLOSED'
    
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING: return 'CONNECTING'
      case WebSocket.OPEN: return 'OPEN'
      case WebSocket.CLOSING: return 'CLOSING'
      case WebSocket.CLOSED: return 'CLOSED'
      default: return 'UNKNOWN'
    }
  }

  isConnectionOpen() {
    return this.isConnected && this.socket && this.socket.readyState === WebSocket.OPEN
  }
}

// Create and export a singleton instance
const websocketService = new WebSocketService()
export default websocketService

// Named exports for convenience
export const ws = websocketService